import { useQuery } from "@tanstack/react-query";
import { api, FESTIVAL_META } from "@/lib/api";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Copy, ArrowLeft, LogOut, Trash2 } from "lucide-react";

const FEST_TABS = [
  { id: "", label: "Overall" },
  { id: "roskilde", label: "Roskilde" },
  { id: "northside", label: "NorthSide" },
  { id: "syd_for_solen", label: "Syd for Solen" },
  { id: "tinderbox", label: "Tinderbox" },
  { id: "smukfest", label: "Smukfest" },
];

export default function LeagueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [festivalId, setFestivalId] = useState("");
  const { data: league } = useQuery({ queryKey: ["league", id], queryFn: () => api.get(`/leagues/${id}`).then(r => r.data) });
  const { data: lb } = useQuery({
    queryKey: ["league-lb", id, festivalId],
    queryFn: () => api.get(`/leagues/${id}/leaderboard`, { params: festivalId ? { festival_id: festivalId } : {} }).then(r => r.data),
    enabled: !!id,
  });

  const copyLink = () => {
    if (!league) return;
    const url = `${window.location.origin}/join/${league.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite-link kopieret");
  };

  const leave = async () => {
    if (!window.confirm("Er du sikker på at du vil forlade denne liga?")) return;
    try {
      await api.post(`/leagues/${id}/leave`);
      toast.success("Forlod liga");
      navigate("/leagues");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const del = async () => {
    if (!window.confirm("Slet denne liga permanent?")) return;
    try {
      await api.delete(`/leagues/${id}`);
      toast.success("Slettet");
      navigate("/leagues");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  if (!league) return <div className="max-w-4xl mx-auto p-8 text-zinc-400">Indlæser…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/leagues" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6" data-testid="league-back">
        <ArrowLeft size={14} /> Alle ligaer
      </Link>
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <div className="label-eyebrow">Privat liga</div>
          <h1 className="hero-title text-4xl sm:text-5xl mt-2" data-testid="league-title">{league.name}</h1>
          <div className="mt-3 flex items-center gap-4 text-sm text-zinc-400">
            <span className="flex items-center gap-1"><Users size={12} /> {league.member_count} medlem{league.member_count === 1 ? "" : "mer"}</span>
            <span className="font-mono tracking-widest text-zinc-300">{league.code}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="league-detail-copy">
            <Copy size={12} className="mr-1" /> Del invite-link
          </Button>
          {league.is_owner ? (
            <Button variant="outline" size="sm" onClick={del} data-testid="league-detail-delete" className="text-red-400 border-red-400/40 hover:bg-red-500/10">
              <Trash2 size={12} className="mr-1" /> Slet
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={leave} data-testid="league-detail-leave">
              <LogOut size={12} className="mr-1" /> Forlad
            </Button>
          )}
        </div>
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto pb-2">
        {FEST_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setFestivalId(t.id)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border ${festivalId === t.id ? "bg-white text-black border-white" : "text-zinc-400 border-white/10 hover:text-white"}`}
            data-testid={`league-tab-${t.id || "overall"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {lb?.locked ? (
        <div className="mt-6 card-flat p-10 text-center" data-testid="league-lb-locked">
          <div className="text-5xl mb-3">🔒</div>
          <div className="font-display font-bold text-xl">Leaderboardet åbner, når deadline er passeret.</div>
          {lb.deadline && <div className="text-zinc-500 text-sm mt-2">Deadline: {new Date(lb.deadline).toLocaleString("da-DK")}</div>}
        </div>
      ) : (
        <div className="mt-6 card-flat overflow-hidden">
          <div className="grid grid-cols-[60px_1fr_auto] gap-3 px-5 py-3 label-eyebrow border-b border-white/5">
            <div>#</div><div>Spiller</div><div className="text-right">Point</div>
          </div>
          {(lb?.entries || (Array.isArray(lb) ? lb : []))?.map(e => {
            const isMe = e.user_id === user?.user_id;
            return (
              <div key={e.user_id} className={`grid grid-cols-[60px_1fr_auto] gap-3 px-5 py-4 items-center ${isMe ? "bg-white/5" : ""} border-b border-white/5`} data-testid={`league-lb-row-${e.position}`}>
                <div className="font-score text-2xl text-zinc-400">{["🥇","🥈","🥉"][e.position - 1] || `#${e.position}`}</div>
                <div className="flex items-center gap-3 min-w-0">
                  {e.picture ? <img src={e.picture} className="w-8 h-8 rounded-full" alt="" /> : <div className="w-8 h-8 rounded-full bg-zinc-800" />}
                  <div className="truncate">{e.display_name}{isMe && <span className="ml-2 text-xs text-orange-400">(dig)</span>}</div>
                </div>
                <div className="text-right font-score text-2xl">{festivalId ? e.points : e.total}</div>
              </div>
            );
          })}
          {(() => { const rows = lb?.entries || (Array.isArray(lb) ? lb : []); return rows.length === 0 && <div className="p-8 text-center text-zinc-500">Ingen medlemmer endnu.</div>; })()}
        </div>
      )}
    </div>
  );
}
